import { z } from 'zod'
import { requiredString } from '../../primitives/string'
import { MESSAGES } from '../../utils/messages'

/**
 * Inmueble (property) — fuente única de verdad para la validación del API
 * (nestjs-zod `createZodDto`) y del formulario web (RHF + zodResolver).
 * Const objects + union types en lugar de enums (convención del repo).
 */

export const AVAILABILITY_STATUS = {
  AVAILABLE: 'AVAILABLE',
  RESERVED: 'RESERVED',
  PENDING_AUTHORIZATION: 'PENDING_AUTHORIZATION',
  IN_NEGOTIATION: 'IN_NEGOTIATION',
  SOLD: 'SOLD'
} as const
export type AvailabilityStatus = (typeof AVAILABILITY_STATUS)[keyof typeof AVAILABILITY_STATUS]

export const UNIT_TYPE = {
  APT: 'APT', // Apartamento
  V: 'V', // Villa
  C: 'C' // Casa
} as const
export type UnitType = (typeof UNIT_TYPE)[keyof typeof UNIT_TYPE]

export const STRUCTURE_TYPE = {
  T: 'T', // Torre
  V: 'V' // Villa
} as const
export type StructureType = (typeof STRUCTURE_TYPE)[keyof typeof STRUCTURE_TYPE]

// ObjectId de Mongo serializado (24 hex): el wire actual identifica
// project/type_property por ObjectId; no cambiar hasta un pase de contrato v2.
const objectIdString = z
  .string({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
  .regex(/^[a-f0-9]{24}$/i, 'Identificador inválido')

export const CreatePropertyInputSchema = z.object({
  name: requiredString(),
  code: requiredString(),
  project: objectIdString,
  // Nombre legacy del wire — se mantiene snake_case hasta contrato v2
  type_property: objectIdString,
  unitType: z.nativeEnum(UNIT_TYPE).optional(),
  tipo: z.string().optional(),
  structureType: z.nativeEnum(STRUCTURE_TYPE).optional(),
  structureNumber: z.number().min(1).optional(),
  identifier: z.number().optional(),
  constructionM2: z.number().optional(),
  terrainM2: z.number().optional(),
  terrainV2: z.number().optional(),
  terraceM2: z.number().optional(),
  parkingM2: z.number().optional(),
  property: z.string().optional(),
  folio: z.string().optional(),
  book: z.string().optional(),
  waterMeter: z.string().optional(),
  electricityMeter: z.string().optional(),
  iusiAmount: z.number().optional(),
  telephone: z.string().optional(),
  active: z.boolean().optional()
})

export const UpdatePropertyInputSchema = CreatePropertyInputSchema.partial()

export type CreatePropertyInput = z.infer<typeof CreatePropertyInputSchema>
export type UpdatePropertyInput = z.infer<typeof UpdatePropertyInputSchema>
